import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Search,
  ChevronDown,
  ArrowRight,
  Layers,
  MapPin,
  Sparkles,
  FolderKanban,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { fetchProjectPhases } from "../../services/projects";
import { getMediaUrl, mapMediaItems } from "../../utils/media";
import { useUser } from "../../context/UserContext";
import LoadingImage from "../../components/LoadingImage";
import { toast } from "react-toastify";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";

const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes

function sortPhasesForDisplay(phases) {
  return [...(phases || [])].sort((a, b) => {
    const ao = a.phaseOrder ?? a.orderIndex ?? a.projectPhaseId ?? a.id ?? 0;
    const bo = b.phaseOrder ?? b.orderIndex ?? b.projectPhaseId ?? b.id ?? 0;
    return Number(ao) - Number(bo);
  });
}

function phaseRowDisplayName(row) {
  const inner = row?.projectPhase || row;
  return (
    inner?.name ||
    row?.name ||
    inner?.phaseName ||
    row?.phaseName ||
    "Unnamed phase"
  );
}

function formatProjectAddress(project) {
  if (!project?.address) return "Address not available";
  const a = project.address;
  return `${a.buildingNo} ${a.streetName1}${
    a.streetName2 ? ", " + a.streetName2 : ""
  }, ${a.district}, ${a.city}, ${a.state} ${a.pinCode}`;
}

function phaseCardMatchesQuery(card, queryLower) {
  if (!queryLower) return true;
  const haystack = [
    card.phaseName,
    card.project?.projectName,
    card.formattedAddress,
    card.project?.developer,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(queryLower);
}

function BV_PhaseList() {
  const navigate = useNavigate();
  const [phaseCards, setPhaseCards] = useState([]);
  const [loadingPhaseExpansion, setLoadingPhaseExpansion] = useState(false);
  const [phaseImages, setPhaseImages] = useState({});
  const fetchingPhaseThumbRef = useRef(new Set());
  const [filterText, setFilterText] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const { userInfo } = useUser();

  const getCachedPhaseThumb = (phaseId) => {
    const cached = localStorage.getItem(`media_phase_${phaseId}`);
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      if (!parsed || !parsed.data || !parsed.cachedAt) return null;
      const isStale = Date.now() - parsed.cachedAt > CACHE_TTL_MS;
      return isStale ? null : parsed.data;
    } catch (e) {
      console.error("error while parsing cached phase media:", e);
      return null;
    }
  };

  const setCachedPhaseThumb = (phaseId, mediaData) => {
    try {
      const payload = {
        data: mediaData,
        cachedAt: Date.now(),
      };
      localStorage.setItem(`media_phase_${phaseId}`, JSON.stringify(payload));
    } catch (e) {
      console.error("Error storing phase media cache:", e);
    }
  };

  const fetchPhaseThumb = useCallback(async (phaseId) => {
    if (!phaseId || fetchingPhaseThumbRef.current.has(phaseId)) return;

    const cached = getCachedPhaseThumb(phaseId);
    if (cached) {
      setPhaseImages((prev) => ({ ...prev, [phaseId]: cached }));
      return;
    }

    fetchingPhaseThumbRef.current.add(phaseId);
    try {
      const response = await axiosInstance
        .get("/projects/phase/media", {
          params: {
            projectPhaseId: phaseId,
            images: true,
            progress: false,
            guidelines: false,
            boq: false,
          },
        })
        .catch(() => ({ items: [] }));

      const mediaArray =
        response?.items || (Array.isArray(response) ? response : []);
      const [mediaData] = mapMediaItems(mediaArray, "Phase Image");
      setPhaseImages((prev) => ({
        ...prev,
        [phaseId]: mediaData || null,
      }));
      if (mediaData) {
        setCachedPhaseThumb(phaseId, mediaData);
      }
    } catch (e) {
      console.error("Error fetching phase image:", e);
      setPhaseImages((prev) => ({ ...prev, [phaseId]: null }));
    } finally {
      fetchingPhaseThumbRef.current.delete(phaseId);
    }
  }, []);

  // Fetch projects the caller can access — backend filters by
  // accessible_project_ids() derived from rbac_role_assignment, so for a BV
  // user this naturally returns only the projects they're assigned to.
  const fetchProjectsBackend = useCallback(
    async ({ page, page_size }) => {
      try {
        const params = { page, page_size };

        const customerId =
          userInfo?.loginIdentifierDetails?.customer?.customerId ||
          userInfo?.loginIdentifierDetails?.customerId;

        if (customerId) {
          params.customerId = customerId;
        }

        const response = await axiosInstance.get(`/projects`, { params });

        if (response?.items && response.total !== undefined) {
          return response;
        } else if (Array.isArray(response)) {
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          return {
            items: response.slice(startIndex, endIndex),
            total: response.length,
            page,
            page_size,
            total_pages: Math.ceil(response.length / page_size),
            has_next: endIndex < response.length,
            has_previous: page > 1,
          };
        } else {
          return {
            items: [],
            total: 0,
            page,
            page_size,
            total_pages: 0,
            has_next: false,
            has_previous: false,
          };
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        toast.error("Failed to load projects");
        return {
          items: [],
          total: 0,
          page: 1,
          page_size: 24,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        };
      }
    },
    [userInfo]
  );

  const {
    data: projects,
    pagination,
    loading: loadingProjects,
    goToPage,
    changePageSize,
  } = usePagination(fetchProjectsBackend, 24, "", true, false);

  useEffect(() => {
    setSelectedProjectId("");
  }, [pagination.page]);

  // Expand current page of projects into one card per phase
  useEffect(() => {
    if (!projects?.length) {
      setPhaseCards([]);
      setLoadingPhaseExpansion(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPhaseExpansion(true);
      const customerId =
        userInfo?.loginIdentifierDetails?.customer?.customerId ||
        userInfo?.loginIdentifierDetails?.customerId;

      try {
        const perProjectCards = await Promise.all(
          projects.map(async (project) => {
            if (!project?.projectId) return [];
            const params = { projectId: project.projectId };
            if (customerId) params.customerId = customerId;
            const listResponse = await fetchProjectPhases(params);
            let phasesArray = [];
            if (listResponse?.items && Array.isArray(listResponse.items)) {
              phasesArray = listResponse.items;
            } else if (Array.isArray(listResponse)) {
              phasesArray = listResponse;
            }
            const sorted = sortPhasesForDisplay(phasesArray);
            const formattedAddress = formatProjectAddress(project);
            return sorted
              .map((row) => {
                const phaseId =
                  row.projectPhaseId ??
                  row.id ??
                  row.projectPhase?.projectPhaseId;
                if (phaseId == null) return null;
                const inner = row?.projectPhase || row;
                return {
                  key: `${project.projectId}-${phaseId}`,
                  project,
                  phaseId,
                  phaseName: phaseRowDisplayName(row),
                  formattedAddress,
                  reraNo: inner?.reraNo || row?.reraNo || null,
                };
              })
              .filter(Boolean);
          })
        );

        if (!cancelled) {
          setPhaseCards(perProjectCards.flat());
        }
      } catch (e) {
        console.error("Error loading phases for list:", e);
        if (!cancelled) {
          setPhaseCards([]);
          toast.error("Failed to load phases");
        }
      } finally {
        if (!cancelled) {
          setLoadingPhaseExpansion(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projects, userInfo]);

  useEffect(() => {
    phaseCards.forEach((card) => {
      if (card.phaseId) {
        fetchPhaseThumb(card.phaseId);
      }
    });
  }, [phaseCards, fetchPhaseThumb]);

  const projectFilterOptions = useMemo(() => {
    if (!projects?.length) return [];
    const seen = new Set();
    const out = [];
    projects.forEach((p) => {
      const id = p?.projectId;
      if (id == null || seen.has(id)) return;
      seen.add(id);
      out.push({
        projectId: id,
        label: p.projectName || `Project ${id}`,
      });
    });
    return out.sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, {
        sensitivity: "base",
      })
    );
  }, [projects]);

  const filteredPhaseCards = useMemo(() => {
    let list = phaseCards;
    if (selectedProjectId !== "") {
      const want = String(selectedProjectId);
      list = list.filter(
        (c) => String(c.project?.projectId) === want
      );
    }
    const q = filterText.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => phaseCardMatchesQuery(c, q));
  }, [phaseCards, filterText, selectedProjectId]);

  // KPI counts based on the current page data
  const kpiCounts = useMemo(() => {
    const cities = new Set();
    const developers = new Set();
    (phaseCards || []).forEach((c) => {
      const city = c.project?.address?.city;
      const dev = c.project?.developer;
      if (city) cities.add(city);
      if (dev) developers.add(dev);
    });
    return {
      phases: phaseCards.length,
      projects: projectFilterOptions.length,
      cities: cities.size,
      developers: developers.size,
    };
  }, [phaseCards, projectFilterOptions]);

  const handlePhaseClick = (phaseId) => {
    if (phaseId != null) {
      navigate(`/bv-user/phases/${phaseId}`);
    }
  };

  useEffect(() => {
    const handleLogout = () => {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith("media_")) {
          localStorage.removeItem(key);
        }
      }
    };

    window.addEventListener("logout", handleLogout);

    return () => {
      window.removeEventListener("logout", handleLogout);
    };
  }, []);

  const showInitialLoading = loadingProjects;
  const showPhaseLoading = !loadingProjects && loadingPhaseExpansion;
  const listEmpty =
    !loadingProjects && !loadingPhaseExpansion && phaseCards.length === 0;
  const filterEmpty =
    !loadingProjects &&
    !loadingPhaseExpansion &&
    phaseCards.length > 0 &&
    filteredPhaseCards.length === 0;

  const hasActiveFilters = filterText.trim() !== "" || selectedProjectId !== "";

  const clearFilters = () => {
    setFilterText("");
    setSelectedProjectId("");
  };

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Slate gradient hero band with KPI strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Buildvizer User
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
                Project Phases
              </h1>
              <p className="mt-1 text-sm text-white/70">
                Phases on the projects assigned to you. Open a phase to review
                details, manage buyers, and track progress.
              </p>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Layers size={13} />
                Phases on page
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpiCounts.phases}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <FolderKanban size={13} />
                Projects
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpiCounts.projects}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <MapPin size={13} />
                Cities
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpiCounts.cities}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Building2 size={13} />
                Developers
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {kpiCounts.developers}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by phase name, project, address…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              disabled={loadingProjects || loadingPhaseExpansion}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <div className="grid grid-cols-1 gap-2 lg:flex lg:items-center lg:gap-2">
            <div className="relative">
              <select
                id="bv-phase-project-filter"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={
                  loadingProjects ||
                  loadingPhaseExpansion ||
                  projectFilterOptions.length === 0
                }
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 disabled:cursor-not-allowed disabled:opacity-60 min-w-[200px]"
              >
                <option value="">All projects on this page</option>
                {projectFilterOptions.map((opt) => (
                  <option key={opt.projectId} value={String(opt.projectId)}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                size={16}
              />
            </div>
          </div>
        </div>

        {/* Result count + clear filters */}
        {!showInitialLoading && !showPhaseLoading && (
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-600">
              Showing{" "}
              <span className="font-semibold text-slate-800">
                {filteredPhaseCards.length}
              </span>{" "}
              {filteredPhaseCards.length === 1 ? "phase" : "phases"}
              {hasActiveFilters && phaseCards.length > 0 && (
                <span className="text-slate-400">
                  {" "}
                  · filtered from {phaseCards.length} on this page
                </span>
              )}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading / empty / grid */}
      {showInitialLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading projects…</p>
        </div>
      ) : projects?.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <FolderKanban className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">
            No projects available yet
          </p>
          <p className="mt-1 text-sm text-slate-500">
            No projects have been assigned to you.
          </p>
        </div>
      ) : showPhaseLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading phases…</p>
        </div>
      ) : listEmpty ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Layers className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">
            No phases on these projects yet
          </p>
          <p className="mt-1 text-sm text-slate-500">
            When phases are added to your assigned projects, they will appear
            here.
          </p>
        </div>
      ) : filterEmpty ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Search className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">
            No phases match your filters
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Try different keywords or choose &quot;All projects on this
            page&quot;.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {filteredPhaseCards.map((card) => {
              const imageUrl = phaseImages[card.phaseId]
                ? getMediaUrl(phaseImages[card.phaseId])
                : null;
              return (
                <div
                  key={card.key}
                  onClick={() => handlePhaseClick(card.phaseId)}
                  className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  {/* Image header */}
                  <div className="relative">
                    {imageUrl ? (
                      <LoadingImage
                        src={imageUrl}
                        alt={card.phaseName}
                        containerClassName="h-40 w-full"
                        className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : fetchingPhaseThumbRef.current.has(card.phaseId) ? (
                      <div className="flex h-40 w-full items-center justify-center bg-slate-100">
                        <div className="h-7 w-7 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
                      </div>
                    ) : (
                      <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <Building2 className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    {/* Top overlay: phase chip (left) */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                        <Layers size={12} />
                        Phase
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                        Assigned
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col p-4">
                    <h3
                      className="truncate text-base font-semibold text-slate-900"
                      title={card.phaseName}
                    >
                      {card.phaseName}
                    </h3>
                    {card.project?.projectName && (
                      <p
                        className="mt-0.5 truncate text-xs font-medium text-slate-600"
                        title={card.project.projectName}
                      >
                        {card.project.projectName}
                      </p>
                    )}

                    {/* Address with icon */}
                    <div className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                      <MapPin
                        size={12}
                        className="mt-0.5 shrink-0 text-slate-400"
                      />
                      <span
                        className="line-clamp-2"
                        title={card.formattedAddress}
                      >
                        {card.formattedAddress}
                      </span>
                    </div>

                    {card.project?.developer && (
                      <p
                        className="mt-1 truncate text-[11px] text-slate-400"
                        title={card.project.developer}
                      >
                        by {card.project.developer}
                      </p>
                    )}

                    {card.reraNo && (
                      <p className="mt-1 text-[11px] text-slate-500">
                        <span className="font-medium text-slate-600">
                          RERA:
                        </span>{" "}
                        {card.reraNo}
                      </p>
                    )}

                    {/* Spacer pushes CTA to the bottom */}
                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                        <span className="text-xs font-medium text-slate-500">
                          View details
                        </span>
                        <ArrowRight
                          size={14}
                          className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-flamingo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inline pagination — bare row, no card chrome. */}
          {!loadingProjects && pagination.total > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
              itemName="phases"
              variant="list"
              showInfo={true}
            />
          )}
        </>
      )}
    </div>
  );
}

export default BV_PhaseList;
